import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Icon, STAR_PATH, type IconName } from './Icon';

/* ---------------- Button ---------------- */
type BtnVariant = 'primary' | 'secondary' | 'paper' | 'quiet' | 'danger' | 'danger-solid';
export function Button({ variant = 'primary', large, block, icon, children, className, ...rest }:
  { variant?: BtnVariant; large?: boolean; block?: boolean; icon?: IconName; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = ['ex-btn', `ex-btn--${variant}`, large && 'ex-btn--lg', block && 'ex-btn--block', className].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest}>{icon && <Icon name={icon} />}{children}</button>;
}

export function IconButton({ icon, label, onBlue, ...rest }: { icon: IconName; label: string; onBlue?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={`ex-iconbtn${onBlue ? ' ex-iconbtn--blue' : ''}`} aria-label={label} {...rest}><Icon name={icon} /></button>;
}

/* ---------------- Badge ---------------- */
export function Badge({ tone = 'blue', children, style }: { tone?: 'blue' | 'gold' | 'paper' | 'ok' | 'warn' | 'bad'; children: ReactNode; style?: CSSProperties }) {
  return <span className={`ex-badge ex-badge--${tone}`} style={style}>{children}</span>;
}

/** Visible marker for sample addresses and other placeholder values. */
export function SampleFlag({ children = 'Sample address', style }: { children?: ReactNode; style?: CSSProperties }) {
  return <span className="ex-sample" style={style}>{children}</span>;
}

/* ---------------- Dossier (paper card) ---------------- */
export function Dossier({ tab, children, style, className, tabStyle, as: Tag = 'section', label }:
  { tab?: string; children: ReactNode; style?: CSSProperties; className?: string; tabStyle?: CSSProperties; as?: 'section' | 'aside' | 'div'; label?: string }) {
  return (
    <Tag className={`ex-dossier${className ? ' ' + className : ''}`} style={{ padding: '26px 18px 16px', marginTop: tab ? 10 : 0, ...style }} aria-label={label}>
      {tab && <span className="ex-dossier__tab" style={tabStyle}>{tab}</span>}
      {children}
    </Tag>
  );
}

/* ---------------- Stamp ---------------- */
export type StampVariant = 'approved' | 'denied' | 'provisional' | 'void' | 'gold';
export function Stamp({ variant, children, sub, animate = true, delay = 0, size, style }:
  { variant: StampVariant; children: ReactNode; sub?: ReactNode; animate?: boolean; delay?: number; size?: number; style?: CSSProperties }) {
  return (
    <span className={`ex-stamp ex-stamp--${variant}${animate ? ' mo-stamp' : ''}`}
      style={{ ['--d' as string]: `${delay}ms`, ...(size ? { fontSize: size } : null), ...style }}>
      {children}{sub && <small>{sub}</small>}
    </span>
  );
}

/* ---------------- Star ring (timer and ornament) ---------------- */
export function ringPoints(lit: number) {
  return Array.from({ length: 12 }, (_, i) => {
    const a = (i * Math.PI) / 6;
    return { x: 50 + 42 * Math.sin(a), y: 50 - 42 * Math.cos(a), lit: i < lit };
  });
}

/**
 * Star-ring timer. Purely presentational: the server supplies remaining/total seconds.
 * 12 stars go out as the clock runs; the last 5 seconds turn maple (numeral always visible).
 */
export function StarRing({ remaining, total, size, numeralSize, showNumber = true, urgentAt = 5, ornament, starSize, children, className, style, label }:
  { remaining?: number; total?: number; size: number; numeralSize?: number; showNumber?: boolean; urgentAt?: number; ornament?: boolean; starSize?: string;
    children?: ReactNode; className?: string; style?: CSSProperties; label?: string }) {
  const frac = total ? Math.max(0, Math.min(1, (remaining ?? 0) / total)) : 1;
  const lit = ornament ? 12 : Math.ceil(frac * 12);
  const urgent = !ornament && total ? (remaining ?? 0) <= urgentAt : false;
  return (
    <div className={`ex-timer${className ? ' ' + className : ''}`} data-urgent={urgent} style={{ width: size, height: size, ...style }}
      role={ornament ? undefined : 'timer'} aria-hidden={ornament ? true : undefined} aria-label={ornament ? undefined : (label ?? `${remaining} seconds left`)}>
      {ringPoints(lit).map((p, i) => (
        <svg key={i} className="ex-timer__star" data-lit={p.lit} viewBox="0 0 24 24" aria-hidden="true"
          style={{ left: `${p.x}%`, top: `${p.y}%`, ...(starSize ? { width: starSize, height: starSize } : null) }}>
          <path className="ex-star" d={STAR_PATH} />
        </svg>
      ))}
      {showNumber && !ornament && <span className="ex-timer__num" style={{ fontSize: numeralSize }}>{remaining}</span>}
      {children}
    </div>
  );
}

/* ---------------- Thinking dots ---------------- */
export const Thinking = () => <span className="ex-thinking" aria-hidden="true"><i /><i /><i /></span>;
